import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { createIntersectionObserver } from '@/utils/performance';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  priority?: boolean;
  quality?: number;
  sizes?: string;
  blurDataURL?: string;
  className?: string;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  priority = false,
  quality = 85,
  sizes = '100vw',
  blurDataURL,
  className,
  onLoad,
  onError,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [error, setError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const [imgSrc, setImgSrc] = useState<string>('');

  // Generate optimized src URLs for different formats and sizes
  const generateOptimizedSrc = (originalSrc: string, targetWidth?: number) => {
    // If it's already a Supabase storage URL, we can add transformation parameters
    if (originalSrc.includes('supabase.co/storage')) {
      const url = new URL(originalSrc);
      if (targetWidth) {
        url.searchParams.set('width', targetWidth.toString());
      }
      url.searchParams.set('quality', quality.toString());
      return url.toString();
    }
    return originalSrc;
  };

  // Generate srcSet for responsive images
  const generateSrcSet = (originalSrc: string) => {
    if (!width) return undefined;
    
    const breakpoints = [640, 768, 1024, 1280, 1920];
    const validBreakpoints = breakpoints.filter(bp => bp <= width * 2);
    
    return validBreakpoints
      .map(bp => `${generateOptimizedSrc(originalSrc, bp)} ${bp}w`)
      .join(', ');
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !imgRef.current) return;

    const observer = createIntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer?.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.1,
      }
    );

    if (observer && imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (observer && imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, [priority]);

  // Set image source when in view
  useEffect(() => {
    if (isInView && src) {
      setImgSrc(generateOptimizedSrc(src, width));
    }
  }, [isInView, src, width, quality]);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setIsLoaded(true);
    onLoad?.(e);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setError(true);
    onError?.(e);
  };

  // Placeholder while loading
  const placeholder = blurDataURL || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIzNSIgeT0iMzUiIHdpZHRoPSIzMCIgaGVpZ2h0PSIzMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiI+CjxwYXRoIGQ9Im0yMSAxNS0zLjA5Ni0zLjA5NmExMC45NzUgMTAuOTc1IDAgMCAwIDEuNjM4LTUuNzIyIDEwLjk3NSAxMC45NzUgMCAwIDAtMTEuMDU3IDEwLjcwOEwzIDIxeiIvPgo8cGF0aCBkPSJtOSAxMS4yNDUgMy4yNDUgMy4yNDVtLTMuMjQ1IDMuMjQ1IDMuMjQ1LTMuMjQ1Ii8+Cjwvc3ZnPgo8L3N2Zz4K';

  if (error) {
    return (
      <div 
        className={cn(
          "flex items-center justify-center bg-muted text-muted-foreground",
          className
        )}
        style={{ width, height }}
      >
        <span className="text-sm">Failed to load image</span>
      </div>
    );
  }

  // Calculate aspect ratio to prevent layout shift
  const aspectRatio = width && height ? (height / width) * 100 : undefined;

  return (
    <div 
      className={cn(
        "relative overflow-hidden", 
        !aspectRatio && "min-h-[200px]",
        className
      )} 
      ref={imgRef}
      style={{
        paddingBottom: aspectRatio ? `${aspectRatio}%` : undefined,
        ...(aspectRatio && { height: 0 })
      }}
    >
      {/* Blur placeholder - only show when aspect ratio container exists */}
      {!isLoaded && aspectRatio && (
        <img
          src={placeholder}
          alt=""
          className={cn(
            "absolute inset-0 w-full h-full object-cover filter blur-sm scale-110 transition-opacity duration-300",
            isLoaded ? "opacity-0" : "opacity-100"
          )}
          aria-hidden="true"
        />
      )}
      
      {/* Main image */}
      {isInView && imgSrc && (
        <img
          {...props}
          src={imgSrc}
          srcSet={generateSrcSet(src)}
          sizes={sizes}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            aspectRatio ? "absolute inset-0" : "relative",
            "w-full h-full transition-opacity duration-300",
            isLoaded ? "opacity-100" : "opacity-0"
          )}
        />
      )}
    </div>
  );
};

export default OptimizedImage;